import ModalSelectWithLabel from "@/components/form/hook-form/modal-select-with-label";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { useMemo } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";
import { HeightSelectModal } from "./height-select-modal";

type HeightModalSelectProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  valueName: Path<T>;
  unitName: Path<T>;
  error?: string;
};

export const HeightModalSelect = <T extends FieldValues>({
  form,
  valueName,
  unitName,
  error,
}: HeightModalSelectProps<T>) => {
  const watchedValue = useWatch({
    control: form.control,
    name: valueName,
  }) as string;
  const watchedUnit = useWatch({
    control: form.control,
    name: unitName,
  }) as string;
  const label = useMemo(() => {
    if (!watchedValue) return "Choose...";
    return `${watchedValue} ${watchedUnit || "cm"}`;
  }, [watchedUnit, watchedValue]);
  const viewportHeight = useViewportHeight();

  return (
    <ModalSelectWithLabel
      control={form.control}
      name={valueName}
      label="Height"
      placeholder="Choose..."
      options={[{ value: watchedValue ?? "", name: label }]}
      error={error}
      initialBreakpoint={410 / viewportHeight}
      renderModalContent={({ onSelect, close }) => (
        <HeightSelectModal
          defaultValue={watchedValue || "170"}
          defaultUnit={(watchedUnit as "cm" | "inches") || "cm"}
          onSelect={(val, unit) => {
            onSelect(val);
            form.setValue(unitName, unit as PathValue<T, Path<T>>, {
              shouldDirty: true,
              shouldTouch: true,
            });
            close();
          }}
        />
      )}
    />
  );
};

export default HeightModalSelect;
